import React, { useState } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Fade } from '@mui/material';
import { useDashboard } from '../hooks/useDashboard';
import PurchaseFilter from '../components/purchaseAnalytics/PurchaseFilter';
import PurchaseOverview from '../components/purchaseAnalytics/PurchaseOverview';
import PurchaseTrendChart from '../components/purchaseAnalytics/PurchaseTrendChart';
import PurchaseTopSuppliers from '../components/purchaseAnalytics/PurchaseTopSuppliers';
import { useWarehouseContext } from '../context/WarehouseContext';
import { useTranslation } from 'react-i18next';

const PurchaseAnalytics = () => {
    const { t } = useTranslation();
    const {
        getPurchaseOverview,
        getPurchaseTrend,
        getPurchaseTopSuppliers
    } = useDashboard();

    const [period, setPeriod] = useState('last_30_days');

    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
            from_date: startOfMonth.toISOString().split('T')[0],
            to_date: endOfMonth.toISOString().split('T')[0],
        };
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedWarehouseIds } = useWarehouseContext();

    const [data, setData] = useState({
        overview: null,
        trend: null,
        suppliers: null
    });

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({ ...prev, [field]: value }));
    };

    const handlePeriodChange = (value) => {
        setPeriod(value);
    };

    const fetchingRef = React.useRef(false);

    const fetchData = React.useCallback(async (force = false) => {
        if (period === 'custom' && (!dateRange.from_date || !dateRange.to_date)) return;
        if (fetchingRef.current) return;

        const warehouseKey = selectedWarehouseIds?.length > 0 ? `_w${selectedWarehouseIds.join('_')}` : '';
        const cacheKey = period === 'custom'
            ? `purchaseAnalytics_custom_${dateRange.from_date}_${dateRange.to_date}${warehouseKey}`
            : `purchaseAnalytics_${period}${warehouseKey}`;
        const cachedData = localStorage.getItem(cacheKey);

        if (!force && cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                if (parsed) { setData(parsed); return; }
            } catch (e) {
                localStorage.removeItem(cacheKey);
            }
        }

        fetchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const params = { period };
            if (period === 'custom') {
                params.from_date = dateRange.from_date;
                params.to_date = dateRange.to_date;
            }

            const [overviewRes, trendRes, suppliersRes] = await Promise.all([
                getPurchaseOverview(params),
                getPurchaseTrend(params),
                getPurchaseTopSuppliers(params)
            ]);

            const newError = overviewRes.error || trendRes.error || suppliersRes.error;
            if (newError) setError(newError);

            const newData = {
                overview: overviewRes.data || null,
                trend: trendRes.data || null,
                suppliers: suppliersRes.data || null
            };

            setData(newData);
            localStorage.setItem(cacheKey, JSON.stringify(newData));
        } catch (err) {
            setError(err.message || t('hookErrors.analyticsPage.fetchFailed'));
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [period, dateRange, getPurchaseOverview, getPurchaseTrend, getPurchaseTopSuppliers, selectedWarehouseIds]);

    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWarehouseIds]);

    const strings = t('purchaseAnalytics.page', { returnObjects: true });

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box mb={5}>
                <Typography
                    variant="h3"
                    component="h1"
                    fontWeight="800"
                    sx={{
                        background: 'linear-gradient(45deg, #05445E 30%, #189AB4 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    {strings.title}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {strings.subtitle}
                </Typography>
            </Box>

            {/* Filter */}
            <PurchaseFilter
                fromDate={dateRange.from_date}
                toDate={dateRange.to_date}
                onFromDateChange={(val) => handleDateChange('from_date', val)}
                onToDateChange={(val) => handleDateChange('to_date', val)}
                onSubmit={() => fetchData(true)}
                loading={loading}
                period={period}
                onPeriodChange={handlePeriodChange}
            />

            {error && (
                <Fade in>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {typeof error === 'object' ? JSON.stringify(error) : error}
                    </Alert>
                </Fade>
            )}

            {loading && !data.overview && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            )}

            {data.overview && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    {/* KPI Cards */}
                    <PurchaseOverview data={data.overview} />

                    {/* Charts + Table Row */}
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 420px', minWidth: 320 }}>
                            <PurchaseTrendChart data={data.trend} />
                        </div>
                        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                            <PurchaseTopSuppliers data={data.suppliers} />
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default PurchaseAnalytics;
